import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  ShieldCheck, AlertTriangle, Radar, ArrowUpRight, GitBranch, Lock, Plus,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  BarChart, Bar, Cell,
} from "recharts";

const SEV = {
  danger: "chip-danger",
  warning: "chip-warning",
  success: "chip-success",
  info: "chip-accent",
};

export default function DashboardHome() {
  const [overview, setOverview] = useState(null);
  const [series, setSeries] = useState([]);
  const [activity, setActivity] = useState([]);
  const [senders, setSenders] = useState([]);
  const [enforcement, setEnforcement] = useState([]);

  useEffect(() => {
    (async () => {
      const [o, t, a, s, e] = await Promise.all([
        api.get("/dashboard/overview"),
        api.get("/dashboard/timeseries"),
        api.get("/dashboard/activity?limit=18"),
        api.get("/dashboard/senders"),
        api.get("/dashboard/enforcement"),
      ]);
      setOverview(o.data);
      setSeries(Array.isArray(t.data) ? t.data : []);
      setActivity(Array.isArray(a.data) ? a.data : []);
      setSenders(Array.isArray(s.data) ? s.data : []);
      setEnforcement(Array.isArray(e.data) ? e.data : Object.values(e.data || []));
    })();
  }, []);

  if (!overview) {
    return <div className="text-sm ink-soft">Loading telemetry…</div>;
  }

  if (overview.empty_state) {
    return (
      <div className="panel p-16 text-center max-w-xl mx-auto" data-testid="empty-state">
        <div className="h-16 w-16 rounded-2xl bg-orange-50 flex items-center justify-center mx-auto mb-5">
          <Radar className="h-7 w-7 text-orange-600" strokeWidth={2} />
        </div>
        <h2 className="font-display text-2xl font-bold ink">No verified domains yet</h2>
        <p className="mt-3 ink-soft">Add a domain and complete DNS ownership verification to begin telemetry ingestion.</p>
        <Link to="/dashboard/domains/new" className="btn-accent mt-7" data-testid="empty-add-domain">
          <Plus className="h-4 w-4" /> Add domain
        </Link>
      </div>
    );
  }

  const kpis = [
    { label: "Trust Score", value: overview.trust_score, suffix: "", icon: ShieldCheck },
    { label: "DMARC Compliance", value: overview.dmarc_compliance, suffix: "%", icon: ShieldCheck },
    { label: "SPF Health", value: overview.spf_health, suffix: "%", icon: GitBranch },
    { label: "DKIM Alignment", value: overview.dkim_alignment, suffix: "%", icon: Lock },
    { label: "Active Threats", value: overview.active_threats, suffix: "", icon: AlertTriangle, danger: true },
    { label: "Enforcement Readiness", value: overview.enforcement_readiness, suffix: "%", icon: Radar },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-home">
      <div>
        <h1 className="font-display text-3xl font-bold ink">Overview</h1>
        <p className="mt-1 ink-soft text-sm">Real-time email trust telemetry across your domains.</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {kpis.map((k) => <Kpi key={k.label} {...k} />)}
      </div>

      {/* Chart + enforcement */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 panel p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="font-display text-lg font-bold ink">Authentication telemetry</h3>
              <p className="text-xs ink-muted mt-0.5">Pass / fail / spoof — last 14 days</p>
            </div>
            <div className="text-xs ink-soft flex gap-3 font-medium">
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-orange-500"></span>pass</span>
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-stone-700"></span>fail</span>
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-red-600"></span>spoof</span>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={series} margin={{ top: 5, right: 5, bottom: 5, left: -10 }}>
                <defs>
                  <linearGradient id="gp" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#FF5C1F" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#FF5C1F" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#E8E3D8" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} stroke="#948B7F" fontSize={10} tickLine={false} axisLine={false} />
                <YAxis stroke="#948B7F" fontSize={10} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{ background: "#fff", border: "1px solid #E8E3D8", borderRadius: 8, fontSize: 12, fontFamily: "Plus Jakarta Sans", boxShadow: "0 4px 16px rgba(0,0,0,0.06)" }}
                  labelStyle={{ color: "#6B6359", fontSize: 11 }}
                />
                <Area type="monotone" dataKey="pass" stroke="#FF5C1F" strokeWidth={2} fill="url(#gp)" />
                <Area type="monotone" dataKey="fail" stroke="#3D3833" strokeWidth={1.5} fill="transparent" />
                <Area type="monotone" dataKey="spoof" stroke="#B91C1C" strokeWidth={1.5} fill="transparent" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="panel p-6">
          <h3 className="font-display text-lg font-bold ink">Enforcement</h3>
          <p className="text-xs ink-muted mt-0.5 mb-5">Policy progression by domain</p>
          <div className="space-y-4">
            {enforcement.map((d) => (
              <div key={d.domain}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-sm font-medium ink truncate">{d.domain}</div>
                  <span className="chip chip-accent text-[10px]">p={d.policy}</span>
                </div>
                <div className="flex gap-1">
                  {[1, 2, 3].map((stage) => (
                    <div
                      key={stage}
                      className={`flex-1 h-1.5 rounded-full ${
                        stage <= d.stage
                          ? stage === 3 ? "bg-emerald-600" : stage === 2 ? "bg-orange-500" : "bg-stone-400"
                          : "bg-stone-200"
                      }`}
                    />
                  ))}
                </div>
                <div className="mt-1.5 flex justify-between text-[10px] ink-muted font-medium uppercase tracking-wider">
                  <span>none</span><span>quarantine</span><span>reject</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Sender + activity */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 panel p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="font-display text-lg font-bold ink">Sender graph</h3>
              <p className="text-xs ink-muted mt-0.5">Volume by sender — last 24h</p>
            </div>
            <div className="text-xs ink-soft flex gap-3 font-medium">
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-orange-500"></span>saas</span>
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-600"></span>internal</span>
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-red-600"></span>suspicious</span>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={senders} margin={{ left: -20 }}>
                <CartesianGrid stroke="#E8E3D8" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" stroke="#948B7F" fontSize={9} tickLine={false} axisLine={false} angle={-25} textAnchor="end" height={70} />
                <YAxis stroke="#948B7F" fontSize={10} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{ background: "#fff", border: "1px solid #E8E3D8", borderRadius: 8, fontSize: 12, fontFamily: "Plus Jakarta Sans", boxShadow: "0 4px 16px rgba(0,0,0,0.06)" }}
                  cursor={{ fill: "rgba(255, 92, 31, 0.06)" }}
                />
                <Bar dataKey="volume" radius={[6, 6, 0, 0]}>
                  {senders.map((s, i) => (
                    <Cell key={i} fill={s.kind === "suspicious" ? "#B91C1C" : s.kind === "saas" ? "#FF5C1F" : "#2E7D5B"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="panel p-6 flex flex-col">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="font-display text-lg font-bold ink">Live activity</h3>
              <p className="text-xs ink-muted mt-0.5">Recent events</p>
            </div>
            <span className="pulse-dot"></span>
          </div>
          <div className="space-y-3 overflow-y-auto max-h-[20rem] -mx-1 px-1" data-testid="activity-feed">
            {activity.map((e) => (
              <div key={e.id} className="border-l-2 border-stone-200 pl-3 py-1 hover:border-orange-400 transition-colors">
                <div className="flex items-start justify-between gap-2">
                  <div className="text-sm font-medium ink">{e.message}</div>
                  <span className={`chip ${SEV[e.severity]} text-[10px] shrink-0`}>{e.severity}</span>
                </div>
                <div className="mt-1 text-xs ink-muted font-mono">
                  {e.domain} · {e.source_ip} {e.sender ? `· ${e.sender}` : ""}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, suffix, icon: Icon, danger }) {
  return (
    <div className="panel p-5" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-medium uppercase tracking-wider ink-muted">{label}</div>
        <div className={`h-7 w-7 rounded-lg flex items-center justify-center ${danger ? "bg-red-50" : "bg-orange-50"}`}>
          <Icon className={`h-3.5 w-3.5 ${danger ? "text-red-600" : "text-orange-600"}`} strokeWidth={2} />
        </div>
      </div>
      <div className="font-display text-3xl font-bold ink">
        {value}
        <span className="text-base ink-muted ml-0.5 font-medium">{suffix}</span>
      </div>
      <div className="mt-2 flex items-center gap-1 text-xs ink-soft">
        <ArrowUpRight className="h-3 w-3 text-emerald-600" />
        <span>vs last 7d</span>
      </div>
    </div>
  );
}
